import type { Change } from "./generated";
import type { SyncStatus } from "./taskApi";

/** Derives the sync indicator from the changes a client reports and from explicit syncs. */
export class SyncStatusTracker {
  private current: SyncStatus = { state: "idle" };
  private readonly listeners = new Set<(status: SyncStatus) => void>();

  constructor(private readonly now: () => Date = () => new Date()) {}

  status(): SyncStatus {
    return this.current;
  }

  onStatusChange(listener: (status: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  observe(change: Change): void {
    if (change === "Remote") {
      this.set({ state: "synced", at: this.now() });
    } else if (typeof change === "object") {
      this.set({ state: "offline", reason: change.SyncFailed.reason });
    }
  }

  async track<T>(sync: () => Promise<T>): Promise<T> {
    this.set({ state: "syncing" });
    try {
      const result = await sync();
      this.set({ state: "synced", at: this.now() });
      return result;
    } catch (failure) {
      this.set({
        state: "offline",
        reason: failure instanceof Error ? failure.message : String(failure),
      });
      throw failure;
    }
  }

  private set(status: SyncStatus): void {
    this.current = status;
    this.listeners.forEach((listener) => listener(status));
  }
}
